import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export enum Language {
  English = 'en',
}

export interface Messages {
  [key: string]: string;
}

type Translations = Record<Language, Messages>;

interface LocalizationContextType {
  locale: Language;
  messages: Messages;
  switchLanguage: (language: Language) => void;
}

interface LocalizationProviderProps {
  children: ReactNode;
  defaultLanguage?: Language;
  translations: Translations;
}

const LocalizationContext = createContext<LocalizationContextType | null>(null);

export const LocalizationProvider = ({
  children,
  defaultLanguage = Language.English,
  translations,
}: LocalizationProviderProps) => {
  const [locale, setLocale] = useState<Language>(defaultLanguage);
  const [messages, setMessages] = useState<Messages>(
    translations[defaultLanguage] ?? {}
  );

  const switchLanguage = useCallback(
    (language: Language) => {
      if (language in translations) {
        setLocale(language);
        setMessages(translations[language]);
      }
    },
    [translations]
  );

  useEffect(() => {
    switchLanguage(defaultLanguage);
  }, [defaultLanguage, switchLanguage]);

  const contextValue = useMemo(
    () => ({
      locale,
      messages,
      switchLanguage,
    }),
    [locale, messages, switchLanguage]
  );

  return (
    <LocalizationContext.Provider value={contextValue}>
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = () => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error(
      'useLocalization must be used within a LocalizationProvider'
    );
  }
  return context;
};
